import { CheckCircle2, Clock, Info, TriangleAlert } from 'lucide-react';
import { useEffect, useRef } from 'react';

import AccimotoExpiredCard from '@/components/accimoto/accimoto-expired-card';
import AccimotoPickupLocationForm from '@/components/accimoto/accimoto-pickup-location-form';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    useAccimotoLink,
    useAccimotoProposal,
    useAnswerAccimoto,
    useUpdateAccimotoPickupLocation,
} from '@/hooks/api/use-accimoto';
import { ApiError } from '@/lib/api/api-error';
import { getApiErrorMessage } from '@/lib/api/client';
import { formatDate } from '@/lib/dates';
import type { AccimotoLink, AccimotoProposal } from '@/types/api/accimoto';

/** Pages ouvertes par les liens des mails `accimoto_accord` (accepter, refuser) et `accimoto_ok`. */
export type AccimotoPage = 'accepter' | 'refuser' | 'localisation-vehicule';

const LinkError = ({ error }: { error: unknown }) => {
    const status = error instanceof ApiError ? error.httpStatus : 0;
    const message =
        status === 403
            ? "Ce lien n'est pas valide ou n'est plus actif. Utilisez le dernier mail reçu ou contactez votre conseiller."
            : status === 404
              ? "Aucune proposition de prise en charge par Accimoto n'a été trouvée pour ce sinistre."
              : 'Une erreur est survenue. Merci de réessayer ultérieurement.';

    return (
        <Alert variant="destructive">
            <TriangleAlert className="w-4 h-4" />
            <AlertDescription>{message}</AlertDescription>
        </Alert>
    );
};

interface AcceptedProps {
    link: AccimotoLink;
    page: AccimotoPage;
    proposal: AccimotoProposal;
}

const Accepted = ({ link, page, proposal }: AcceptedProps) => {
    const pickup = useUpdateAccimotoPickupLocation(link);
    const location = proposal.pickup_location;

    // Lieu enregistré (à l'instant ou lors d'une visite précédente) : plus rien à saisir.
    if (location !== null) {
        return (
            <Alert variant="success">
                <CheckCircle2 className="w-4 h-4" />
                <AlertDescription>
                    <p>
                        Le lieu où se situe votre véhicule a bien été enregistré. Il sera transmis à Accimoto pour
                        l'enlèvement.
                    </p>
                    <p className="mt-2">
                        {location.name}, {location.address}, {location.postal_code} {location.town}
                    </p>
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            {page === 'accepter' && (
                <Alert variant="success">
                    <CheckCircle2 className="w-4 h-4" />
                    <AlertDescription>Merci pour votre réponse. Votre choix a bien été pris en compte.</AlertDescription>
                </Alert>
            )}

            {page === 'refuser' && (
                <Alert>
                    <Info className="w-4 h-4" />
                    <AlertDescription>
                        Vous avez déjà accepté la prise en charge de votre véhicule par Accimoto.
                    </AlertDescription>
                </Alert>
            )}

            {pickup.isError && (
                <Alert variant="destructive">
                    <TriangleAlert className="w-4 h-4" />
                    <AlertDescription>
                        {getApiErrorMessage(pickup.error) || 'Une erreur est survenue. Merci de réessayer ultérieurement.'}
                    </AlertDescription>
                </Alert>
            )}

            {proposal.expires_at && (
                <Alert variant="warning">
                    <Clock className="w-4 h-4" />
                    <AlertDescription>
                        Merci d'indiquer où se trouve votre véhicule avant le{' '}
                        {formatDate(proposal.expires_at, "dd/MM/yyyy 'à' HH'h'mm")}.
                    </AlertDescription>
                </Alert>
            )}

            <AccimotoPickupLocationForm
                pickupLocation={null}
                loading={pickup.isPending}
                onSubmit={(form) => pickup.mutate(form)}
            />
        </div>
    );
};

const Proposal = ({ link, page, proposal }: AcceptedProps) => {
    switch (proposal.status) {
        case 'accepte':
            return <Accepted link={link} page={page} proposal={proposal} />;
        case 'expire':
            return <AccimotoExpiredCard proposal={proposal} />;
        case 'refuse':
            return (
                <Card>
                    <CardHeader>
                        <CardTitle>Merci pour votre réponse</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p>Votre refus de la prise en charge de votre véhicule par Accimoto est bien enregistré.</p>
                        <p>Votre dossier poursuit son traitement selon le parcours habituel.</p>
                        <p>
                            Nous vous invitons à prendre les dispositions nécessaires à l'enlèvement de votre véhicule dans
                            les meilleurs délais afin de limiter les frais de gardiennage qui seront à votre charge.
                        </p>
                    </CardContent>
                </Card>
            );
        default:
            return (
                <Alert>
                    <Info className="w-4 h-4" />
                    <AlertDescription>
                        Vous n'avez pas encore répondu à la proposition de prise en charge de votre véhicule par
                        Accimoto. Utilisez les liens du mail reçu pour l'accepter ou la refuser.
                    </AlertDescription>
                </Alert>
            );
    }
};

/**
 * Réponse du client à la proposition Accimoto, puis saisie du lieu d'enlèvement, sans connexion.
 *
 * La réponse n'est envoyée qu'une fois la page exécutée dans le navigateur : les antivirus de
 * messagerie qui ouvrent les liens du mail pour les analyser n'enregistrent donc rien.
 */
const AccimotoReponse = ({ page }: { page: AccimotoPage }) => {
    const link = useAccimotoLink();
    const answer = page === 'accepter' ? 'accept' : page === 'refuser' ? 'refuse' : null;
    const { mutate: sendAnswer, isIdle, isPending, error: answerError } = useAnswerAccimoto(link);
    // Pages de réponse : l'état arrive avec la réponse (mis en cache), pas de lecture préalable.
    const query = useAccimotoProposal(link, answer === null);
    const sent = useRef(false);

    useEffect(() => {
        if (answer === null || sent.current) return;
        sent.current = true;
        sendAnswer(answer);
    }, [answer, sendAnswer]);

    const error = answer === null ? query.error : answerError;
    const loading = answer === null ? query.isPending : isIdle || isPending;

    if (error) return <LinkError error={error} />;
    if (loading || !query.data) return <SmallSpinner />;

    return <Proposal link={link} page={page} proposal={query.data} />;
};

export default AccimotoReponse;
